import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Tag, Percent, Users, Plus, Pencil, Trash2,
  CheckCircle, XCircle, RefreshCw, DollarSign, Search
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminDiscountGroups({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [groups, setGroups] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  
  const [groupForm, setGroupForm] = useState({
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_percentage: 0,
    is_active: true
  });
  
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const results = await Promise.allSettled([
        axios.get(`${API}/admin/client-groups`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/clients`, { headers: getAuthHeaders() })
      ]);
      if (results[0].status === 'fulfilled') setGroups(results[0].value.data);
      if (results[1].status === 'fulfilled') {
        const clientsList = Array.isArray(results[1].value.data) ? results[1].value.data : (results[1].value.data.clients || []);
        setClients(clientsList);
      }
    } catch (error) {
      toast.error('Не удалось загрузить данные');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name) {
      toast.error('Укажите название группы');
      return;
    }
    
    try {
      if (editingGroup) {
        await axios.put(`${API}/admin/client-groups/${editingGroup.id}`, groupForm, {
          headers: getAuthHeaders()
        });
        toast.success('Группа обновлена');
      } else {
        await axios.post(`${API}/admin/client-groups`, groupForm, {
          headers: getAuthHeaders()
        });
        toast.success('Группа создана');
      }
      setShowGroupDialog(false);
      resetGroupForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось сохранить группу');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Удалить группу? Клиенты потеряют привязку к ней.')) return;
    
    try {
      await axios.delete(`${API}/admin/client-groups/${groupId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Группа удалена');
      fetchData();
    } catch (error) {
      toast.error('Не удалось удалить группу');
    }
  };

  const handleAssignClient = async () => {
    if (!selectedClientId) {
      toast.error('Выберите клиента');
      return;
    }
    
    try {
      await axios.put(
        `${API}/admin/clients/${selectedClientId}/group?group_id=${selectedGroup.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Клиент добавлен в группу');
      setShowAssignDialog(false);
      setSelectedClientId('');
      setClientSearch('');
      fetchData();
    } catch (error) {
      toast.error('Не удалось назначить группу');
    }
  };

  const handleRemoveFromGroup = async (clientId) => {
    try {
      await axios.put(
        `${API}/admin/clients/${clientId}/group?group_id=`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Клиент удалён из группы');
      fetchData();
    } catch (error) {
      toast.error('Не удалось удалить из группы');
    }
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      discount_type: group.discount_type || 'percentage',
      discount_percentage: group.discount_percentage || 0,
      is_active: group.is_active !== false
    });
    setShowGroupDialog(true);
  };

  const openAssignDialog = (group) => {
    setSelectedGroup(group);
    setShowAssignDialog(true);
  };

  const resetGroupForm = () => {
    setEditingGroup(null);
    setGroupForm({
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_percentage: 0,
      is_active: true
    });
  };

  const getGroupClients = (groupId) => {
    return clients.filter(c => c.client_group_id === groupId);
  };

  const getClientsWithoutGroup = () => {
    return clients.filter(c => !c.client_group_id);
  };

  const filteredClientsForAssign = getClientsWithoutGroup().filter(c => {
    if (!clientSearch) return true;
    const search = clientSearch.toLowerCase();
    return (
      c.email?.toLowerCase().includes(search) ||
      c.company_name?.toLowerCase().includes(search) ||
      c.full_name?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(currentLang === 'en' ? 'en-US' : currentLang === 'kz' ? 'kk-KZ' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">{t('common.loading')}...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin/settings`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Управление
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('discountGroups.title')}</h1>
                <p className="text-sm text-gray-500">{t('discountGroups.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button 
                onClick={() => {
                  resetGroupForm();
                  setShowGroupDialog(true);
                }}
                className="bg-emerald-700 hover:bg-emerald-800"
                data-testid="create-group-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('discountGroups.createGroup')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">{t('discountGroups.totalGroups')}</div>
                  <div className="text-2xl font-bold">{groups.length}</div>
                </div>
                <Tag className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">{t('discountGroups.clientsWithGroups')}</div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {clients.filter(c => c.client_group_id).length}
                  </div>
                </div>
                <Users className="w-8 h-8 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-500">{t('discountGroups.clientsWithoutGroups')}</div>
                  <div className="text-2xl font-bold text-gray-400">
                    {getClientsWithoutGroup().length}
                  </div>
                </div>
                <Users className="w-8 h-8 text-gray-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Groups List */}
        {groups.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Tag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('discountGroups.noGroups')}</h3>
              <p className="text-gray-500 mb-4">
                {t('discountGroups.createFirstGroup')}
              </p>
              <Button 
                onClick={() => setShowGroupDialog(true)}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('discountGroups.createGroup')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => {
              const groupClients = getGroupClients(group.id);
              
              return (
                <Card key={group.id} data-testid={`group-card-${group.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          group.is_active !== false ? 'bg-emerald-100' : 'bg-gray-100'
                        }`}>
                          {group.discount_type === 'percentage' ? (
                            <Percent className={`w-6 h-6 ${
                              group.is_active !== false ? 'text-emerald-600' : 'text-gray-400'
                            }`} />
                          ) : (
                            <DollarSign className={`w-6 h-6 ${
                              group.is_active !== false ? 'text-emerald-600' : 'text-gray-400'
                            }`} />
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {group.name}
                            {group.is_active === false && (
                              <Badge variant="outline" className="text-gray-500">{t('discountGroups.inactive')}</Badge>
                            )}
                          </CardTitle>
                          <CardDescription>
                            {group.description || t('discountGroups.noDescription')}
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openAssignDialog(group)}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          {t('discountGroups.addClient')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditGroup(group)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteGroup(group.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Discount Info */}
                    <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="text-sm text-gray-500">{t('discountGroups.discountType')}:</span>
                        <span className="ml-2 font-medium">
                          {group.discount_type === 'percentage' ? t('discountGroups.percentageType') : t('discountGroups.fixedType')}
                        </span>
                      </div>
                      {group.discount_type === 'percentage' && group.discount_percentage > 0 && (
                        <div>
                          <span className="text-sm text-gray-500">{t('discountGroups.defaultDiscount')}:</span>
                          <span className="ml-2 font-medium text-emerald-600">
                            {group.discount_percentage}%
                          </span>
                        </div>
                      )}
                      <div className="ml-auto">
                        <Badge>{groupClients.length} {t('discountGroups.clients')}</Badge>
                      </div>
                    </div>

                    {/* Clients in Group */}
                    {groupClients.length > 0 ? (
                      <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-700 mb-2">
                          {t('discountGroups.clientsInGroup')}:
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {groupClients.slice(0, 6).map((client) => (
                            <div 
                              key={client.id}
                              className="flex items-center justify-between p-2 bg-white border rounded-lg"
                            >
                              <div className="min-w-0">
                                <div className="font-medium text-sm truncate">
                                  {client.company_name || client.full_name || t('common.noName')}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{client.email}</div>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="shrink-0 text-red-500 hover:text-red-700"
                                onClick={() => handleRemoveFromGroup(client.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                        {groupClients.length > 6 && (
                          <div className="text-sm text-gray-500 mt-2">
                            ... {t('discountGroups.andMore')} {groupClients.length - 6} {t('discountGroups.clients')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">{t('discountGroups.noClientsInGroup')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-6 bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Tag className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-blue-800 font-medium">{t('discountGroups.howItWorks')}</p>
                <ul className="text-blue-600 text-sm mt-1 space-y-1">
                  <li>• {t('discountGroups.step1')}</li>
                  <li>• {t('discountGroups.step2')}</li>
                  <li>• {t('discountGroups.step3')}</li>
                  <li>• {t('discountGroups.step4')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Group Dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingGroup ? t('discountGroups.editGroup') : t('discountGroups.newGroup')}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveGroup} className="space-y-4">
            <div>
              <Label>{t('discountGroups.groupName')} *</Label>
              <Input
                value={groupForm.name}
                onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('discountGroups.groupNamePlaceholder')}
                required
              />
            </div>

            <div>
              <Label>{t('discountGroups.description')}</Label>
              <Textarea
                value={groupForm.description}
                onChange={(e) => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('discountGroups.descriptionPlaceholder')}
                rows={2}
              />
            </div>

            <div>
              <Label>{t('discountGroups.discountType')}</Label>
              <Select
                value={groupForm.discount_type}
                onValueChange={(v) => setGroupForm(prev => ({ ...prev, discount_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">
                    <div className="flex items-center gap-2">
                      <Percent className="w-4 h-4" />
                      {t('discountGroups.percentageType')}
                    </div>
                  </SelectItem>
                  <SelectItem value="fixed">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      {t('discountGroups.fixedType')}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {groupForm.discount_type === 'percentage' 
                  ? t('discountGroups.percentageNote')
                  : t('discountGroups.fixedNote')
                }
              </p>
            </div>

            {groupForm.discount_type === 'percentage' && (
              <div>
                <Label>{t('discountGroups.defaultDiscountPercent')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={groupForm.discount_percentage}
                  onChange={(e) => setGroupForm(prev => ({ 
                    ...prev, 
                    discount_percentage: parseFloat(e.target.value) || 0 
                  }))}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('discountGroups.defaultDiscountNote')}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="cursor-pointer">{t('discountGroups.groupActive')}</Label>
                <p className="text-xs text-gray-500">{t('discountGroups.groupInactiveNote')}</p>
              </div>
              <Switch
                checked={groupForm.is_active}
                onCheckedChange={(v) => setGroupForm(prev => ({ ...prev, is_active: v }))}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowGroupDialog(false)}>
                {t('discountGroups.cancel')}
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                {editingGroup ? t('discountGroups.save') : t('discountGroups.create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Client Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('discountGroups.addClientToGroup')} "{selectedGroup?.name}"</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>{t('discountGroups.searchClient')}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder={t('discountGroups.searchClientPlaceholder')}
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-2">
              {filteredClientsForAssign.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">
                    {clientSearch ? t('discountGroups.noClientsFound') : t('discountGroups.allClientsInGroups')}
                  </p>
                </div>
              ) : (
                filteredClientsForAssign.slice(0, 20).map((client) => (
                  <div
                    key={client.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedClientId === client.id
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedClientId(client.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium">
                          {client.company_name || client.full_name || t('common.noName')}
                        </div>
                        <div className="text-sm text-gray-500">{client.email}</div>
                      </div>
                      <Badge variant={client.role === 'B2B' ? 'default' : 'secondary'}>
                        {client.role}
                      </Badge>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowAssignDialog(false);
                  setSelectedClientId('');
                  setClientSearch('');
                }}
              >
                {t('discountGroups.cancel')}
              </Button>
              <Button 
                onClick={handleAssignClient}
                disabled={!selectedClientId}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                {t('discountGroups.addToGroup')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminDiscountGroups;
